import * as React from 'react';

import type { PrecheckinWidgetMFEProps } from '../../PrecheckinWidget.types';
import type { UsePrecheckinWidgetMFEReturn } from './usePrecheckinWidgetMFE.types';

/**
 * Hook to load the PrecheckinWidget MFE component
 *
 * Handles dynamic import of the remote module with error handling
 * and calls onLoad callback when load attempt completes
 */
const usePrecheckinWidgetMFE = (
  canViewWidget: boolean,
  onLoad?: () => void
): UsePrecheckinWidgetMFEReturn => {
  const [RemoteComponent, setRemoteComponent] =
    React.useState<React.ComponentType<PrecheckinWidgetMFEProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!canViewWidget) {
      onLoad?.();
      return;
    }

    const loadComponent = async (): Promise<void> => {
      try {
        const { default: Component } =
          await import('access_pass_mfe/PrecheckinWidget');
        setRemoteComponent(() => Component);
      } catch (error) {
        console.error('Error loading PrecheckinWidget:', error);
        setLoadError(error as Error);
      } finally {
        onLoad?.();
      }
    };

    loadComponent();
  }, [canViewWidget, onLoad]);

  return {
    RemoteComponent,
    loadError,
  };
};

export { usePrecheckinWidgetMFE };
